import React, { useCallback, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';
import Calendar from '@/components/Icons/Calendar';

import { UTCDateMini } from '@date-fns/utc';
import classNames from 'classnames';

import Checkbox from '../CheckBox';
import { Input } from '../Input';
import Label from '../shared/Label';

import s from './DatePicker.module.scss';
import parseDateValue, { type DateValue } from './utils/parseDateValue';

export interface DatePickerProps {
  label: string;
  description?: string;
  hint?: string;
  placeholder?: string;
  required?: boolean;
  selected?: Date;
  onChange: (value: DateValue) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  error?: string;
  hasError?: boolean;
  value: DateValue | string | Date | null;
  disabled?: boolean;
  fixed?: boolean;
  min?: Date;
  max?: Date;
  isEditor?: boolean;
}

const CustomDatePicker: React.FC<DatePickerProps> = ({
  label,
  description,
  disabled,
  hint,
  placeholder = 'MM/DD/YYYY',
  required,
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  hasError,
  fixed = true,
  min,
  max,
  isEditor,
}) => {
  // Parse the value to determine if it's a DateValue object or legacy format
  const parsedValue = parseDateValue(value);
  const [inputDate, setInputDate] = useState<Date | null>(parsedValue.date);
  const [text, setText] = useState<string | null>(parsedValue.text);
  const [isText, setIsText] = useState<boolean>(Boolean(parsedValue.text));

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  const handleCommitChange = useCallback((newValue: DateValue) => {
    onChangeRef.current(newValue);
    onBlurRef.current?.();
  }, []);

  const handleBlur = useCallback(() => {
    handleCommitChange({
      date: inputDate,
      text,
    });
  }, [inputDate, text, handleCommitChange]);

  // Memoized function to compare dates safely - handle both Date objects and potential null values
  const compareDates = useCallback((date1: Date | null, date2: Date | null): boolean => {
    if (date1 === null && date2 === null) return true;
    if (!date1 || !date2) return false;
    return new Date(date1).getTime() === new Date(date2).getTime();
  }, []);

  useEffect(() => {
    const newParsedValue = parseDateValue(value);

    // Only update state if the parsed value is actually different from current state
    const currentValue = { date: inputDate, text };
    const newValue = { date: newParsedValue.date, text: newParsedValue.text };

    const datesEqual = compareDates(currentValue.date, newValue.date);
    const textsEqual = currentValue.text === newValue.text;

    if (!datesEqual || !textsEqual) {
      setInputDate(newParsedValue.date);
      setText(newParsedValue.text);
      setIsText(Boolean(newParsedValue.text));
    }
  }, [value]);

  const createUTCDate = (year: number, month: number, day: number): Date => {
    const utcTimestamp = Date.UTC(year, month, day);
    return new UTCDateMini(utcTimestamp);
  };

  const handleDateChange = (date: Date | null): void => {
    if (date) {
      const utcDate = createUTCDate(date.getFullYear(), date.getMonth(), date.getDate());
      setInputDate(utcDate);
      handleCommitChange({
        date: utcDate,
        text,
      });
    } else {
      setInputDate(null);
      handleCommitChange({
        date: null,
        text,
      });
    }
  };

  const handleYearChange = (date: Date): void => {
    if (inputDate && date) {
      const utcDate = createUTCDate(date.getFullYear(), inputDate.getMonth(), inputDate.getDate());
      setInputDate(utcDate);
      handleCommitChange({
        date: utcDate,
        text,
      });
    }
  };

  const handleMonthChange = (date: Date): void => {
    if (inputDate && date) {
      const utcDate = createUTCDate(inputDate.getFullYear(), date.getMonth(), inputDate.getDate());
      setInputDate(utcDate);
      handleCommitChange({
        date: utcDate,
        text,
      });
    }
  };

  const handleOverrideCheckboxChange = useCallback(
    (newIsText: boolean) => {
      setIsText(newIsText);
      if (newIsText) {
        setInputDate(null);
        handleCommitChange({
          date: null,
          text,
        });
      } else {
        setText(null);
        handleCommitChange({
          date: inputDate,
          text: null,
        });
      }
    },
    [inputDate, text, handleCommitChange],
  );

  return (
    <div className={s.dateWrapper}>
      <Label
        label={label}
        required={required}
        description={description}
        errorMessage={error}
        hasError={hasError}
        hint={hint}
      >
        <div className={classNames(s.wrapper, (hasError || !!error) && s.error)}>
          {!isText ? (
            <DatePicker
              showIcon
              maxDate={max}
              minDate={min}
              showPopperArrow={false}
              selected={inputDate instanceof Date && !Number.isNaN(inputDate.getTime()) ? inputDate : null}
              onChange={handleDateChange}
              onFocus={onFocus}
              onBlur={handleBlur}
              placeholderText={placeholder}
              className={s.input}
              required={required}
              icon={<Calendar className={s.icon} />}
              disabled={disabled}
              showYearDropdown
              showMonthDropdown
              onYearChange={handleYearChange}
              onMonthChange={handleMonthChange}
              dropdownMode='select'
              popperProps={
                fixed
                  ? {
                      strategy: 'fixed',
                    }
                  : undefined
              }
            />
          ) : (
            <Input
              value={text || ''}
              onChange={(v) => {
                setText(v);
                handleCommitChange({
                  date: null,
                  text: v,
                });
              }}
              onBlur={handleBlur}
              disabled={disabled}
              placeholder='Enter date as text'
            />
          )}
        </div>
        <div>
          {!disabled && (
            <div className={s.checkboxes}>
              {!isEditor && (
                <Checkbox
                  label='Override with text'
                  size='sm'
                  isChecked={isText}
                  onChange={handleOverrideCheckboxChange}
                />
              )}
            </div>
          )}
        </div>
      </Label>
    </div>
  );
};

export default CustomDatePicker;
